import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GameController, MapPin, Heart, Compass, Lightning, Check,
  ArrowRight, ArrowLeft, X, Users, Trophy, Storefront,
  BookOpen, Bell, Sparkle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { SupportLink as OnboardingSupportLink } from '../components/Support';
import { Logo as OnboardingLogo } from '../components/Logo';
import { useLogoFlash } from '../components/LogoFlash';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

const STEP_ICONS = {
  welcome: Sparkle, favorite_games: Heart, play_style: GameController,
  location: MapPin, interests: Compass, collection_intro: Lightning, done: Check,
};

const PLAY_STYLES = [
  { value: 'casual', label: 'Casual', desc: 'I play for fun, not to win', color: '#10B981' },
  { value: 'social', label: 'Social', desc: 'I play to hang out with friends', color: '#3B82F6' },
  { value: 'competitive', label: 'Competitive', desc: 'I play to win and improve', color: '#F59E0B' },
  { value: 'learning', label: 'Learning', desc: "I'm new and still figuring things out", color: '#9D4CDD' },
];

const INTEREST_OPTIONS = [
  { value: 'beginner_events', label: 'Beginner-Friendly Events', icon: BookOpen, color: '#10B981' },
  { value: 'tournaments', label: 'Tournaments', icon: Trophy, color: '#F59E0B' },
  { value: 'open_play', label: 'Open Play / Drop-In', icon: GameController, color: '#9D4CDD' },
  { value: 'shop_events', label: 'Game Shop Events', icon: Storefront, color: '#3B82F6' },
  { value: 'leagues', label: 'Leagues & Groups', icon: Users, color: '#EC4899' },
];

export default function OnboardingWizard({ onComplete, onSkip }) {
  const [steps, setSteps] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [games, setGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [progRes, gamesRes] = await Promise.all([
          ax().get('/api/onboarding/progress'),
          ax().get('/api/games-browse?limit=20'),
        ]);
        // Defensive: WKWebView can hand axios string bodies. Parse if needed.
        let progData = progRes.data;
        if (typeof progData === 'string') { try { progData = JSON.parse(progData); } catch { progData = {}; } }
        setProgress(progData);
        // games-browse should return an array, but tolerate {games: []} too.
        let gamesData = gamesRes.data;
        if (typeof gamesData === 'string') { try { gamesData = JSON.parse(gamesData); } catch { gamesData = []; } }
        const gamesArr = Array.isArray(gamesData)
          ? gamesData
          : (Array.isArray(gamesData?.games) ? gamesData.games : []);
        setGames(gamesArr);
        const obType = progData?.onboarding_type || 'player';
        const stepsRes = await ax().get(`/api/onboarding/steps?onboarding_type=${obType}`);
        let stepsData = stepsRes.data;
        if (typeof stepsData === 'string') {
          try { stepsData = JSON.parse(stepsData); } catch { stepsData = []; }
        }
        const stepsArr = Array.isArray(stepsData)
          ? stepsData
          : (Array.isArray(stepsData?.steps) ? stepsData.steps : []);
        if (!stepsArr.length) {
          console.warn('Onboarding steps API returned non-array shape:', stepsRes.data);
        }
        setSteps(stepsArr);
        const idx = stepsArr.findIndex(s => s.step_key === progData?.current_step_key);
        if (idx >= 0) setCurrentIdx(idx);
      } catch (err) {
        // If API fails, use default player steps so wizard still renders
        console.warn('Onboarding load failed, using defaults:', err);
        setSteps([
          { step_key: 'welcome', title: 'Welcome to CyBoard', description: 'Discover local board game sessions.', step_order: 1 },
          { step_key: 'done', title: "You're All Set!", description: 'Start exploring!', step_order: 2 },
        ]);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const currentStep = steps[currentIdx];

  const completeStep = useCallback(async (stepKey, data) => {
    try {
      const res = await ax().post('/api/onboarding/steps/complete', { step_key: stepKey, data });
      // Defensive: tolerate string body (WKWebView quirk) and missing fields.
      let body = res.data;
      if (typeof body === 'string') {
        try { body = JSON.parse(body); } catch { body = {}; }
      }
      if (body?.completed) {
        onComplete?.();
      } else if (body?.next_step) {
        const nextIdx = steps.findIndex(s => s.step_key === body.next_step);
        if (nextIdx >= 0) setCurrentIdx(nextIdx);
        else setCurrentIdx((idx) => Math.min(idx + 1, steps.length - 1));
      } else {
        // Fallback: just advance one step.
        setCurrentIdx((idx) => Math.min(idx + 1, steps.length - 1));
      }
    } catch (e) {
      console.warn('completeStep failed', e?.response?.status, e?.message);
      toast.error('Failed to save');
    }
  }, [steps, onComplete]);

  const handleSkip = async () => {
    try {
      await ax().post('/api/onboarding/skip');
      onSkip?.();
    } catch {}
  };

  const goNext = () => {
    if (!currentStep) return;
    let data = null;
    if (currentStep.step_key === 'favorite_games' && selectedGames.length > 0) {
      data = { games: selectedGames };
    } else if (currentStep.step_key === 'play_style' && selectedStyle) {
      data = { play_style: selectedStyle };
    } else if (currentStep.step_key === 'location' && locationInput.trim()) {
      data = { location: locationInput.trim() };
    } else if (currentStep.step_key === 'interests' && selectedInterests.length > 0) {
      data = { interests: selectedInterests };
    }
    completeStep(currentStep.step_key, data);
  };

  const goBack = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };

  if (loading) return (
    <div className="fixed inset-0 z-50 bg-[#06060A] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const totalSteps = steps.length;
  const pct = totalSteps > 0 ? ((currentIdx + 1) / totalSteps) * 100 : 0;
  const StepIcon = currentStep ? (STEP_ICONS[currentStep.step_key] || Sparkle) : Sparkle;

  return (
    <div className="fixed inset-0 z-50 bg-[#06060A] flex flex-col" data-testid="onboarding-wizard">
      {/* Progress bar */}
      <div className="h-1 bg-white/[0.06] w-full">
        <motion.div className="h-full bg-[#9D4CDD]" animate={{ width: `${pct}%` }} transition={{ duration: 0.3 }} />
      </div>

      {/* Skip button */}
      <div className="flex justify-end px-4 py-3">
        <button onClick={handleSkip} className="text-[#555] hover:text-white text-sm transition-colors" data-testid="onboarding-skip">
          Skip Setup <X size={14} className="inline ml-0.5" />
        </button>
      </div>

      {/* Step Content */}
      <div className="flex-1 flex items-center justify-center px-4 pb-8 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep?.step_key || 'loading'}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-md"
          >
            {currentStep?.step_key === 'welcome' && <WelcomeStep onboardingType={progress?.onboarding_type} />}
            {currentStep?.step_key === 'favorite_games' && (
              <FavoriteGamesStep games={games} selected={selectedGames} setSelected={setSelectedGames} />
            )}
            {currentStep?.step_key === 'play_style' && (
              <PlayStyleStep selected={selectedStyle} setSelected={setSelectedStyle} />
            )}
            {currentStep?.step_key === 'location' && (
              <LocationStep value={locationInput} setValue={setLocationInput} />
            )}
            {currentStep?.step_key === 'interests' && (
              <InterestsStep selected={selectedInterests} setSelected={setSelectedInterests} />
            )}
            {currentStep?.step_key === 'collection_intro' && <CollectionIntroStep />}
            {currentStep?.step_key === 'shop_profile' && <ShopProfileStep />}
            {currentStep?.step_key === 'supported_games' && <ShopGamesStep />}
            {currentStep?.step_key === 'first_event' && <ShopFirstEventStep />}
            {currentStep?.step_key === 'discovery_tips' && <ShopDiscoveryStep />}
            {currentStep?.step_key === 'done' && <DoneStep onboardingType={progress?.onboarding_type} />}
            {currentStep && !['welcome','favorite_games','play_style','location','interests','collection_intro','shop_profile','supported_games','first_event','discovery_tips','done'].includes(currentStep.step_key) && (
              <GenericStep title={currentStep.title} description={currentStep.description} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="px-4 pb-6 max-w-md mx-auto w-full">
        <div className="flex items-center gap-3">
          {currentIdx > 0 && (
            <button onClick={goBack} className="p-3 rounded-xl bg-white/[0.04] text-[#888] hover:text-white transition-colors" data-testid="onboarding-back">
              <ArrowLeft size={18} />
            </button>
          )}
          <button
            onClick={goNext}
            className="flex-1 py-3.5 rounded-xl bg-[#9D4CDD] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#8B3DC7] active:scale-[0.98] transition-all"
            data-testid="onboarding-next"
          >
            {currentStep?.step_key === 'done' ? 'Start Exploring' : 'Continue'}
            <ArrowRight size={16} />
          </button>
        </div>
        {/* Step dots */}
        <div className="flex justify-center gap-1.5 mt-4">
          {steps.map((s, i) => (
            <div key={s.step_key} className={`h-1.5 rounded-full transition-all ${
              i === currentIdx ? 'w-6 bg-[#9D4CDD]' : i < currentIdx ? 'w-1.5 bg-[#9D4CDD]/40' : 'w-1.5 bg-white/[0.08]'
            }`} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Step Components ──

function WelcomeStep({ onboardingType }) {
  const isShop = onboardingType === 'shop_manager';
  return (
    <div className="text-center" data-testid="step-welcome">
      <div className="flex justify-center mb-5">
        <OnboardingLogo variant="full" size="lg" clickable={false} showTagline={false} />
      </div>
      <h1 className="text-2xl font-bold text-white mb-3 mt-2" style={{ fontFamily: 'Unbounded' }}>
        {isShop ? 'Welcome to CyBoard for Shops' : 'Welcome to CyBoard'}
      </h1>
      <p className="text-[#888] text-base leading-relaxed max-w-sm mx-auto">
        {isShop
          ? 'Set up your shop to appear in discovery and connect with local board game players.'
          : 'Find local board game sessions, connect with players, join tournaments, and discover game shops near you.'}
      </p>
      <div className="flex justify-center gap-6 mt-8">
        {isShop ? (
          <>
            {[
              { icon: Users, label: 'Attract Players', color: '#3B82F6' },
              { icon: Lightning, label: 'Create Events', color: '#F59E0B' },
              { icon: Trophy, label: 'Run Tournaments', color: '#10B981' },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon size={22} weight="duotone" style={{ color: item.color }} />
                </div>
                <span className="text-[11px] text-[#666]">{item.label}</span>
              </div>
            ))}
          </>
        ) : (
          <>
            {[
              { icon: Users, label: 'Find Players', color: '#3B82F6' },
              { icon: Trophy, label: 'Tournaments', color: '#F59E0B' },
              { icon: Storefront, label: 'Game Shops', color: '#10B981' },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon size={22} weight="duotone" style={{ color: item.color }} />
                </div>
                <span className="text-[11px] text-[#666]">{item.label}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function FavoriteGamesStep({ games, selected, setSelected }) {
  const toggle = (name) => {
    setSelected(prev => prev.includes(name) ? prev.filter(g => g !== name) : [...prev, name]);
  };
  return (
    <div data-testid="step-favorite-games">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center mx-auto mb-4">
          <Heart size={28} weight="duotone" className="text-[#F59E0B]" />
        </div>
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Pick Your Games</h2>
        <p className="text-[#888] text-sm mt-2">Choose games you love — we'll match you with sessions</p>
      </div>
      <div className="grid grid-cols-2 gap-2 max-h-[40vh] overflow-y-auto">
        {games.map(g => {
          const active = selected.includes(g.name);
          return (
            <button key={g.id} onClick={() => toggle(g.name)}
              className={`p-3 rounded-lg border text-left transition-all ${
                active ? 'border-[#F59E0B]/40 bg-[#F59E0B]/[0.06]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
              }`} data-testid={`game-pick-${g.slug}`}>
              <p className="text-sm text-white font-medium truncate">{g.name}</p>
              <p className="text-[10px] text-[#555] mt-0.5">{g.category}</p>
              {active && <Check size={14} className="text-[#F59E0B] mt-1" />}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && <p className="text-center text-[#F59E0B] text-xs mt-3">{selected.length} games selected</p>}
    </div>
  );
}

function PlayStyleStep({ selected, setSelected }) {
  return (
    <div data-testid="step-play-style">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
          <GameController size={28} weight="duotone" className="text-[#10B981]" />
        </div>
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>How Do You Play?</h2>
        <p className="text-[#888] text-sm mt-2">This helps us find the right events for you</p>
      </div>
      <div className="space-y-2">
        {PLAY_STYLES.map(ps => (
          <button key={ps.value} onClick={() => setSelected(ps.value)}
            className={`w-full p-4 rounded-xl border text-left transition-all ${
              selected === ps.value ? `border-[${ps.color}]/40 bg-[${ps.color}]/[0.06]` : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
            }`}
            style={selected === ps.value ? { borderColor: `${ps.color}60`, backgroundColor: `${ps.color}0a` } : {}}
            data-testid={`style-${ps.value}`}>
            <p className="text-white font-semibold text-sm">{ps.label}</p>
            <p className="text-[#888] text-xs mt-0.5">{ps.desc}</p>
            {selected === ps.value && <Check size={14} style={{ color: ps.color }} className="mt-1" />}
          </button>
        ))}
      </div>
    </div>
  );
}

function LocationStep({ value, setValue }) {
  return (
    <div data-testid="step-location">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mx-auto mb-4">
          <MapPin size={28} weight="duotone" className="text-[#3B82F6]" />
        </div>
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Where Are You?</h2>
        <p className="text-[#888] text-sm mt-2">Help us find events and shops near you</p>
      </div>
      <input
        value={value} onChange={e => setValue(e.target.value)}
        placeholder="City, State (e.g. Austin, TX)"
        className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#3B82F6]/40"
        data-testid="location-input"
      />
      <p className="text-[#444] text-xs mt-2 text-center">This is optional — you can skip or change it later</p>
    </div>
  );
}

function InterestsStep({ selected, setSelected }) {
  const toggle = (val) => {
    setSelected(prev => prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val]);
  };
  return (
    <div data-testid="step-interests">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-xl bg-[#EC4899]/10 flex items-center justify-center mx-auto mb-4">
          <Compass size={28} weight="duotone" className="text-[#EC4899]" />
        </div>
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>What Interests You?</h2>
        <p className="text-[#888] text-sm mt-2">Pick what you want to see in your feed</p>
      </div>
      <div className="space-y-2">
        {INTEREST_OPTIONS.map(opt => {
          const active = selected.includes(opt.value);
          return (
            <button key={opt.value} onClick={() => toggle(opt.value)}
              className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition-all ${
                active ? 'border-white/[0.15] bg-white/[0.04]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]'
              }`}
              style={active ? { borderColor: `${opt.color}40`, backgroundColor: `${opt.color}08` } : {}}
              data-testid={`interest-${opt.value}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${opt.color}15` }}>
                <opt.icon size={18} weight="duotone" style={{ color: opt.color }} />
              </div>
              <span className="text-sm text-white font-medium flex-1 text-left">{opt.label}</span>
              {active && <Check size={16} style={{ color: opt.color }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CollectionIntroStep() {
  return (
    <div className="text-center" data-testid="step-collection-intro">
      <div className="w-14 h-14 rounded-xl bg-[#9D4CDD]/10 flex items-center justify-center mx-auto mb-4">
        <Lightning size={28} weight="duotone" className="text-[#9D4CDD]" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>Your Game Collection</h2>
      <p className="text-[#888] text-sm leading-relaxed max-w-sm mx-auto mb-6">
        Track games you own, want, or have played. Shops and sessions use your collection to match you with the right events.
      </p>
      <div className="space-y-2 text-left max-w-xs mx-auto">
        {[
          { label: 'Mark games as Favorite for priority alerts', color: '#F59E0B' },
          { label: 'Add to Wishlist to hear about demos', color: '#9D4CDD' },
          { label: 'Flag games you can bring to events', color: '#10B981' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <Check size={14} style={{ color: item.color }} />
            <span className="text-[#888]">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DoneStep({ onboardingType }) {
  const isShop = onboardingType === 'shop_manager';
  return (
    <div className="text-center" data-testid="step-done">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="w-20 h-20 rounded-2xl bg-[#39FF14]/10 flex items-center justify-center mx-auto mb-6"
      >
        <Check size={40} weight="bold" className="text-[#39FF14]" />
      </motion.div>
      <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>
        {isShop ? "Your Shop is Live!" : "You're All Set!"}
      </h2>
      <p className="text-[#888] text-base max-w-sm mx-auto mb-6">
        {isShop
          ? 'Players can now discover your shop and RSVP to your events.'
          : 'Start exploring sessions, check out nearby shops, and join the community.'}
      </p>
      <div className="flex justify-center gap-4">
        {isShop ? (
          <>
            {[
              { label: 'Dashboard', icon: Storefront, color: '#10B981' },
              { label: 'Events', icon: Lightning, color: '#F59E0B' },
              { label: 'Analytics', icon: Compass, color: '#3B82F6' },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center gap-1.5">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon size={20} weight="duotone" style={{ color: item.color }} />
                </div>
                <span className="text-[10px] text-[#666]">{item.label}</span>
              </div>
            ))}
          </>
        ) : (
          <>
            {[
              { label: 'Discover', icon: Compass, color: '#3B82F6' },
              { label: 'Shops', icon: Storefront, color: '#10B981' },
              { label: 'Alerts', icon: Bell, color: '#F59E0B' },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center gap-1.5">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                  <item.icon size={20} weight="duotone" style={{ color: item.color }} />
                </div>
                <span className="text-[10px] text-[#666]">{item.label}</span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Support footer for the final step */}
      <div className="mt-8 pt-5 border-t border-white/[0.04]">
        <p className="text-[12px] text-[#666]">
          Need help getting started? Reach us at{' '}
          <OnboardingSupportLink subject="CyBoard Onboarding Help" label="support@cyboard.games" className="text-[#9D4CDD]" />.
        </p>
      </div>
    </div>
  );
}

// ── Shop Onboarding Steps ──

function ShopProfileStep() {
  return (
    <div className="text-center" data-testid="step-shop-profile">
      <div className="w-14 h-14 rounded-xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
        <Storefront size={28} weight="duotone" className="text-[#10B981]" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>Complete Your Profile</h2>
      <p className="text-[#888] text-sm leading-relaxed max-w-sm mx-auto mb-6">
        Add your shop name, location, hours, and what you offer. A complete profile helps players find you.
      </p>
      <div className="space-y-2 text-left max-w-xs mx-auto">
        {['Shop name & description', 'Location & hours', 'Contact info & social links', 'Cover photo'].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <Check size={14} className="text-[#10B981]" />
            <span className="text-[#888]">{item}</span>
          </div>
        ))}
      </div>
      <p className="text-[#555] text-xs mt-4">You can edit this anytime in Shop Settings</p>
    </div>
  );
}

function ShopGamesStep() {
  return (
    <div className="text-center" data-testid="step-supported-games">
      <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center mx-auto mb-4">
        <GameController size={28} weight="duotone" className="text-[#F59E0B]" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>Your Games</h2>
      <p className="text-[#888] text-sm leading-relaxed max-w-sm mx-auto mb-6">
        Add games your shop supports — this helps players find you when searching for specific games.
      </p>
      <div className="space-y-2 text-left max-w-xs mx-auto">
        {['Add games to your shop library', 'Mark games available for demos', 'Set up game inventory'].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <Check size={14} className="text-[#F59E0B]" />
            <span className="text-[#888]">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShopFirstEventStep() {
  return (
    <div className="text-center" data-testid="step-first-event">
      <div className="w-14 h-14 rounded-xl bg-[#9D4CDD]/10 flex items-center justify-center mx-auto mb-4">
        <Lightning size={28} weight="duotone" className="text-[#9D4CDD]" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>Create Your First Event</h2>
      <p className="text-[#888] text-sm leading-relaxed max-w-sm mx-auto mb-6">
        Post a game night, open play, or demo night. Events are the best way to attract new players.
      </p>
      <div className="space-y-2 text-left max-w-xs mx-auto">
        {[
          { text: 'Use Quick Create for 1-tap events', color: '#9D4CDD' },
          { text: 'Tournaments drive competitive players', color: '#F59E0B' },
          { text: 'Learn-to-play nights grow your community', color: '#10B981' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <Check size={14} style={{ color: item.color }} />
            <span className="text-[#888]">{item.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShopDiscoveryStep() {
  return (
    <div className="text-center" data-testid="step-discovery-tips">
      <div className="w-14 h-14 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mx-auto mb-4">
        <Compass size={28} weight="duotone" className="text-[#3B82F6]" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>Get Discovered</h2>
      <p className="text-[#888] text-sm leading-relaxed max-w-sm mx-auto mb-6">
        Tips to make your shop visible to nearby players:
      </p>
      <div className="space-y-2 text-left max-w-xs mx-auto">
        {[
          'Keep your profile and hours up to date',
          'Post events regularly — active shops rank higher',
          'Enable discovery in your shop settings',
          'Respond to RSVPs to build trust',
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <Check size={14} className="text-[#3B82F6]" />
            <span className="text-[#888]">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function GenericStep({ title, description }) {
  return (
    <div className="text-center" data-testid="step-generic">
      <div className="w-14 h-14 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
        <Sparkle size={28} weight="duotone" className="text-[#888]" />
      </div>
      <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>{title}</h2>
      <p className="text-[#888] text-sm leading-relaxed max-w-sm mx-auto">{description}</p>
    </div>
  );
}


// ── Activation Checklist Component ──
//
// BUG FIX NOTES:
//   Previously this component:
//     1. Only fetched on mount (useEffect [] deps) — stale state after user action elsewhere.
//     2. Sliced tasks to 3 incomplete + 2 completed — hiding newly completed items.
//     3. Disappeared at 100% (return null) — felt broken to the user.
//   Fix: refetches on window focus, visibility change, and a global `cyboard:checklist-refresh`
//   event. Shows ALL tasks with animated strikethrough for completed. Celebratory state at 100%.

const CHECKLIST_REFRESH_EVENT = 'cyboard:checklist-refresh';
export function triggerChecklistRefresh() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHECKLIST_REFRESH_EVENT));
}

// Axios response interceptor: when the user performs any checklist-advancing action
// anywhere in the app, automatically notify the checklist to refetch.
// Attached ONCE per session (guarded by a global flag).
const CHECKLIST_REFRESH_URL_PATTERNS = [
  /\/api\/users\/profile/,
  /\/api\/collections(\?|$|\/)/,
  /\/api\/event-rsvps/,
  /\/api\/events\/[^/]+\/rsvp/,
  /\/api\/saved-alerts/,
  /\/api\/rules\/ask/,
  /\/api\/ai\/ask/,
  /\/api\/shop-portal\/[^/]+\/events/,
  /\/api\/shop-portal\/[^/]+\/open-play/,
  /\/api\/shop-portal\/[^/]+\/profile/,
];
if (typeof window !== 'undefined' && !window.__cyboardChecklistInterceptorAttached) {
  window.__cyboardChecklistInterceptorAttached = true;
  axios.interceptors.response.use(
    (response) => {
      try {
        const cfg = response.config || {};
        const url = cfg.url || '';
        const method = (cfg.method || 'get').toLowerCase();
        if (['post', 'put', 'patch', 'delete'].includes(method) &&
            CHECKLIST_REFRESH_URL_PATTERNS.some(r => r.test(url))) {
          // Fire after the caller's handlers run.
          setTimeout(() => window.dispatchEvent(new Event(CHECKLIST_REFRESH_EVENT)), 250);
        }
      } catch { /* never block responses */ }
      return response;
    },
    (err) => Promise.reject(err)
  );
}

export function ActivationChecklist() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const prevCompletedRef = React.useRef(new Set());
  const hasLoadedOnceRef = React.useRef(false);
  const wasFullyDoneRef = React.useRef(false);
  const [bursts, setBursts] = useState([]); // [{id, xp, taskKey}]
  const { flash, FlashRenderer } = useLogoFlash();
  const routerNavigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await ax().get('/api/onboarding/activation-tasks');
      const newData = r.data;
      const nowCompleted = new Set(
        (newData.tasks || []).filter(t => t.completed).map(t => t.task_key)
      );
      // Only fire bursts AFTER the initial load completes, so we don't
      // celebrate tasks that were already done before the user even opened the page.
      if (hasLoadedOnceRef.current) {
        const prev = prevCompletedRef.current;
        const newlyDone = (newData.tasks || []).filter(
          t => t.completed && !prev.has(t.task_key)
        );
        if (newlyDone.length > 0) {
          const base = Date.now();
          setBursts(b => [
            ...b,
            ...newlyDone.map((t, i) => ({ id: base + i, xp: t.xp, taskKey: t.task_key })),
          ]);
          newlyDone.forEach((_, i) => {
            setTimeout(() => {
              setBursts(b => b.filter(x => x.id !== base + i));
            }, 1600);
          });
        }
      }
      prevCompletedRef.current = nowCompleted;
      hasLoadedOnceRef.current = true;
      // Detect 0..99% → 100% transition for level-up flash.
      const isFullyDone = (newData?.progress_pct === 100);
      if (isFullyDone && !wasFullyDoneRef.current && data) {
        flash({ message: 'LEVEL UP', tone: 'level-up' });
      }
      wasFullyDoneRef.current = isFullyDone;
      setData(newData);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    const onVisible = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(CHECKLIST_REFRESH_EVENT, onFocus);
    const interval = setInterval(load, 30000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(CHECKLIST_REFRESH_EVENT, onFocus);
      clearInterval(interval);
    };
  }, [load]);

  if (!data || dismissed) return null;

  const tasks = Array.isArray(data.tasks) ? data.tasks : [];
  const total = data.total ?? tasks.length;
  const completed = data.completed ?? tasks.filter(t => t?.completed).length;
  const progressPct = data.progress_pct ?? (total ? Math.round((completed / total) * 100) : 0);
  const allDone = progressPct === 100 && total > 0;
  // Use React Router SPA navigation, not window.location.href.
  // The old `window.location.href` triggered a full document reload — on
  // Capacitor (iOS/Android) this can break the WebView state and force a
  // re-bootstrap of the app. SPA navigation also preserves auth/scroll state.
  const navigate = (url) => { if (url) routerNavigate(url); };

  // Resolve task → target route. Shop-related tasks deep-link into the
  // user's actual shop if we know it; otherwise fall back to /shops which
  // shows the "my shops" banner + register-shop CTA.
  const shopId = (data && data.home_shop_id) || null;
  const shopRoute = (sub) => shopId ? `/shop/${shopId}${sub ? `/${sub}` : ''}` : '/shops';

  const TASK_ACTIONS = {
    complete_profile: '/profile/edit',
    pick_favorite_games: '/profile/edit',
    set_location: '/profile/edit',
    add_collection_game: '/collection',
    join_first_session: '/discover',
    save_first_alert: '/notifications/saved-alerts',
    view_rules: '/rules',
    complete_shop_profile: shopRoute('profile'),
    create_first_event: shopRoute('events'),
    post_open_play: shopRoute('open-play'),
    add_game_library: shopRoute('game-library'),
  };

  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-4 mb-4 relative overflow-hidden" data-testid="activation-checklist">
      <FlashRenderer />
      {/* XP bursts — fly up from checklist body to the level/xp badge in the top bar */}
      <AnimatePresence>
        {bursts.map(b => (
          <motion.div
            key={b.id}
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: [0, 1, 1, 0], y: -140, scale: 1.1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="pointer-events-none absolute left-1/2 bottom-8 -translate-x-1/2 z-20"
            data-testid={`xp-burst-${b.taskKey}`}
          >
            <div className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#9D4CDD]/40 bg-[#9D4CDD]/15 backdrop-blur-sm shadow-lg shadow-[#9D4CDD]/20">
              <Sparkle size={12} weight="fill" className="text-[#F59E0B]" />
              <span className="text-[13px] font-bold text-[#F59E0B] tabular-nums">+{b.xp} XP</span>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>

      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-white" data-testid="checklist-title">
            {allDone ? 'All set! 🎉' : 'Getting Started'}
          </p>
          {allDone && <p className="text-[11px] text-[#10B981] mt-0.5">Your account is fully activated.</p>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[#9D4CDD] tabular-nums" data-testid="checklist-progress-count">
            {completed}/{total}
          </span>
          {allDone && (
            <button onClick={() => setDismissed(true)} className="text-[#555] hover:text-white p-0.5"
              data-testid="checklist-dismiss" aria-label="Dismiss">
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden mb-3" data-testid="checklist-progress-bar">
        <div className="h-full bg-[#9D4CDD] rounded-full transition-all duration-500"
          style={{ width: `${progressPct}%` }} />
      </div>
      <div className="space-y-1.5">
        {tasks.map(t => {
          const target = TASK_ACTIONS[t.task_key];
          const clickable = !t.completed && target;
          return (
            <motion.div
              key={t.task_key}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => clickable && navigate(target)}
              className={`flex items-center gap-2 text-[12px] rounded-md py-1 px-1 -mx-1 transition-colors ${
                t.completed ? 'text-[#555]' : clickable ? 'text-[#888] hover:text-white hover:bg-white/[0.02] cursor-pointer' : 'text-[#888]'
              }`}
              data-testid={`task-${t.task_key}`}
              data-completed={t.completed ? 'true' : 'false'}>
              {t.completed ? (
                <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }}
                  className="w-4 h-4 rounded-full bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                  <Check size={10} weight="bold" className="text-[#10B981]" />
                </motion.div>
              ) : (
                <div className="w-4 h-4 rounded-full border border-white/[0.12] flex-shrink-0" />
              )}
              <span className={t.completed ? 'line-through' : ''}>{t.label}</span>
              <span className={`text-[9px] ml-auto tabular-nums ${t.completed ? 'text-[#333]' : 'text-[#9D4CDD]'}`}>
                +{t.xp}xp
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
